import { COMMUNICATION_API, STORAGE_KEYS } from "../constants/communicationConstants";
import axiosInstance from "./api";
import {
  CommunicationDefaults,
  CommunicationPreferencesOverview,
  CommunicationSetupStatus,
  CsPreferencesUpdateNotice,
  DailyDigest,
  DeliveryHistoryFilters,
  DeliveryHistoryItem,
  DeliveryHistoryPreview,
  OrganizationPreferenceDetail,
  SaveDefaultsPayload,
  SaveOrganizationPreferencePayload,
} from "./models/communication/communicationPreferences.model";
import { mapNotificationsDigestToDailyDigest } from "../utils/helpers/communicationPreferencesHelpers";

function authHeaders(token: string) {
  return {
    Authorization: `Bearer ${token}`,
  };
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

class CommunicationPreferencesService {
  getOverview(token: string): Promise<CommunicationPreferencesOverview> {
    return axiosInstance
      .get(COMMUNICATION_API.overview, {
        headers: authHeaders(token),
      })
      .then((res) => res.data.data);
  }

  getDefaults(token: string): Promise<CommunicationDefaults> {
    return axiosInstance
      .get(COMMUNICATION_API.defaults, {
        headers: authHeaders(token),
      })
      .then((res) => res.data.data);
  }

  saveDefaults(
    token: string,
    payload: SaveDefaultsPayload
  ): Promise<CommunicationDefaults> {
    return axiosInstance
      .put(COMMUNICATION_API.defaults, payload, {
        headers: authHeaders(token),
      })
      .then((res) => res.data.data);
  }

  getOrganization(
    token: string,
    organizationId: number | string
  ): Promise<OrganizationPreferenceDetail> {
    return axiosInstance
      .get(COMMUNICATION_API.organization(organizationId), {
        headers: authHeaders(token),
      })
      .then((res) => res.data.data);
  }

  saveOrganization(
    token: string,
    organizationId: number | string,
    payload: SaveOrganizationPreferencePayload
  ): Promise<OrganizationPreferenceDetail> {
    return axiosInstance
      .put(COMMUNICATION_API.organization(organizationId), payload, {
        headers: authHeaders(token),
      })
      .then((res) => res.data.data);
  }

  restoreOrganizationDefaults(
    token: string,
    organizationId: number | string
  ): Promise<OrganizationPreferenceDetail> {
    return axiosInstance
      .post(
        COMMUNICATION_API.restoreDefaults(organizationId),
        {},
        {
          headers: authHeaders(token),
        }
      )
      .then((res) => res.data.data);
  }

  setExternalNotifications(
    token: string,
    organizationId: number | string,
    enabled: boolean
  ): Promise<OrganizationPreferenceDetail> {
    return axiosInstance
      .put(
        COMMUNICATION_API.externalNotifications(organizationId),
        { enabled },
        {
          headers: authHeaders(token),
        }
      )
      .then((res) => res.data.data);
  }

  getDeliveryHistory(
    token: string,
    filters: DeliveryHistoryFilters = {}
  ): Promise<DeliveryHistoryItem[]> {
    const params: Record<string, string | number> = {};
    if (
      filters.organizationId !== undefined &&
      filters.organizationId !== "" &&
      filters.organizationId !== "all"
    ) {
      params.organizationId = Number(filters.organizationId);
    }
    if (filters.channel && filters.channel !== "all") {
      params.channel = filters.channel;
    }
    if (filters.status && filters.status !== "all") {
      params.status = filters.status;
    }
    if (filters.limit) {
      params.limit = filters.limit;
    }

    return axiosInstance
      .get(COMMUNICATION_API.deliveryHistory, {
        headers: authHeaders(token),
        params,
      })
      .then((res) => res.data.data ?? []);
  }

  getDeliveryHistoryItem(
    token: string,
    id: number | string
  ): Promise<DeliveryHistoryPreview> {
    return axiosInstance
      .get(COMMUNICATION_API.deliveryHistoryItem(id), {
        headers: authHeaders(token),
      })
      .then((res) => res.data.data);
  }

  getSetupStatus(userId: number): CommunicationSetupStatus {
    return {
      isComplete: readJson(STORAGE_KEYS.setupComplete(userId), false),
    };
  }

  markSetupComplete(userId: number): void {
    writeJson(STORAGE_KEYS.setupComplete(userId), true);
  }

  getCsUpdateNotice(userId: number): CsPreferencesUpdateNotice {
    return readJson(STORAGE_KEYS.csUpdated(userId), { visible: false });
  }

  dismissCsUpdateNotice(userId: number): void {
    writeJson(STORAGE_KEYS.csUpdated(userId), { visible: false });
  }

  getDailyDigest(
    token: string,
    organizationId?: number | string | null
  ): Promise<DailyDigest> {
    const params: Record<string, number> = {};
    if (
      organizationId !== undefined &&
      organizationId !== null &&
      organizationId !== "" &&
      organizationId !== "all"
    ) {
      params.organizationId = Number(organizationId);
    }

    return axiosInstance
      .get(COMMUNICATION_API.digest, {
        headers: authHeaders(token),
        params,
      })
      .then((res) => mapNotificationsDigestToDailyDigest(res.data.data));
  }
}

export default new CommunicationPreferencesService();
